import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';

import { Box, Avatar, Typography, ListItemText } from '@mui/material';
import { fDateTime } from 'src/utils/format-time';
import { fCurrency } from 'src/utils/format-number';
import { Label } from 'src/components/label';
import type { GridRow } from '../BalanceTab';
import { InvoiceStage } from 'src/types/invoices';
import type { IClient } from 'src/types/clients';
import { useTranslate } from 'src/locales';
import { ProductNames } from 'src/types/common';

type OwnProps = {
  row: GridRow;
  selected: boolean;
  rowIndex: number;
  onSortChange?: (field: string, direction: 'asc' | 'desc') => void;
  currentCurrency: string;
  clientData: IClient;
  page?: number;
  onPageChange?: (page: number) => void;
};

export function TransactionsTable({ row, selected, rowIndex, clientData }: OwnProps) {
  const { t } = useTranslate();
  const getStageColor = (stage: string) => {
    switch (stage) {
      case InvoiceStage.Invoiced:
        return 'info';
      case InvoiceStage.Reminded:
        return 'warning';
      case InvoiceStage.DebtCollection:
        return 'error';
      default:
        return 'info';
    }
  };

  const getOwnerAvatar = () => {
    const ownerName = row.owner || clientData.name;

    if (ownerName === ProductNames.Inio) {
      return {
        src: '/logo/inio-signature.svg',
        alt: ProductNames.Inio,
        name: ProductNames.Inio,
        bgcolor: '#F1F1F1',
      };
    } else if (ownerName === ProductNames.I2C) {
      return {
        src: '/favicons/ecollect/favicon.ico',
        alt: ProductNames.I2C,
        name: ProductNames.I2C,
      };
    } else {
      return {
        src: clientData.logo_uri || '',
        alt: clientData.name,
        name: ownerName,
        bgcolor: '#9DA4AB',
      };
    }
  };

  const ownerInfo = getOwnerAvatar();

  return (
    <TableRow hover selected={selected} data-test-id={`row-${rowIndex}`}>
      {/* Created At */}
      <TableCell>
        <ListItemText
          primary={fDateTime(row.createdAt, 'DD MMM YYYY')}
          primaryTypographyProps={{
            typography: 'body2',
            fontWeight: 400,
            noWrap: true,
          }}
        />
        <ListItemText
          primary={fDateTime(row.createdAt, 'h:mm A')}
          primaryTypographyProps={{
            typography: 'caption',
            color: 'text.secondary',
            noWrap: true,
          }}
        />
      </TableCell>

      {/* Transaction ID */}
      <TableCell>
        <Typography
          style={{ wordBreak: 'break-word' }}
          variant="body2"
          fontWeight={400}
          fontSize={16}
        >
          {row.id}
        </Typography>
      </TableCell>

      {/* Transaction Reference */}
      <TableCell>
        <Typography
          variant="body2"
          style={{ wordBreak: 'break-word' }}
          fontWeight={400}
          fontSize={16}
        >
          {row.transactionReference}
        </Typography>
      </TableCell>

      {/* Type */}
      <TableCell>
        <Typography variant="body2">{row.transactionType}</Typography>
      </TableCell>

      {/* Stage */}
      <TableCell align="center">
        <Label variant="soft" color={getStageColor(row.stage)} sx={{ width: 100 }}>
          {row.stage === '' ? 'N/A' : t(`pages.content.invoiceStages.${row.stage}`)}
        </Label>
      </TableCell>

      {/* Amount */}
      <TableCell align="left" sx={{ fontWeight: 500 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
          }}
        >
          {fCurrency(row.amount, { minimumFractionDigits: 2, currency: row.currency })}
        </Typography>
      </TableCell>

      {/* Owner */}
      <TableCell>
        <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
          <Avatar
            alt={ownerInfo.alt}
            src={ownerInfo.src}
            sx={{
              bgcolor: ownerInfo.bgcolor,
              width: 32,
              height: 32,
              fontSize: '0.875rem',
            }}
          />
          <Typography variant="body2" fontWeight={400}>
            {ownerInfo.name}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );
}
